import { QrCode, Search } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useAuth } from "react-oidc-context";
import { useNavigate } from "react-router";
import { apiGet, apiSend } from "../api";
import type { PortalConfig } from "../config";
import type { Lookups } from "../types";
import { Modal } from "./ticket/Modal";

interface Address { id: string; street: string; street2: string | null; city: string; state: string; zip: string }
interface Customer { id: string; firstName: string | null; lastName: string | null; email: string; phone: string; isWholesale: boolean; addresses: Address[] }

const addrLine = (a: Address) => [a.street, a.street2, `${a.city}, ${a.state} ${a.zip}`].filter(Boolean).join(", ");
const nameOf = (c: Customer) => [c.firstName, c.lastName].filter(Boolean).join(" ") || c.email;

/**
 * Add Ticket (Joe, 2026-11-03), as the old portal's ticket form: Wholesale Customers quick keys, "use existing
 * customer" search, or a new customer; the address (one of theirs or a new one); location (WRP by default); job
 * number and name; description. Saving buys label 1 and emails it, as the old system did (see new-ticket.ts).
 */
export function AddTicketDialog({ config, lookups, onClose }: { config: PortalConfig; lookups: Lookups | null; onClose: () => void }) {
  const auth = useAuth();
  const token = auth.user?.id_token ?? "";
  const navigate = useNavigate();
  const [wholesale, setWholesale] = useState<Customer[]>([]);
  const [existing, setExisting] = useState(false);
  const [q, setQ] = useState("");
  const [hits, setHits] = useState<Customer[]>([]);
  const [picked, setPicked] = useState<Customer | null>(null);
  const [addressId, setAddressId] = useState<string>("new");
  const [c, setC] = useState({ firstName: "", lastName: "", email: "", phone: "", isWholesale: false });
  const [a, setA] = useState({ street: "", street2: "", city: "", state: "", zip: "" });
  const shop = lookups?.locations.find((l) => l.kind === "shop");
  const [t, setT] = useState({ locationId: shop?.id ?? "", jobNumber: "", jobName: "", description: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    apiGet<{ customers: Customer[] }>(config, token, "/customers?wholesale=1").then((r) => setWholesale(r.customers), () => setWholesale([]));
  }, [config, token]);
  useEffect(() => {
    if (!existing || q.trim().length < 3) { setHits([]); return; }
    const ctl = new AbortController();
    const h = setTimeout(() => {
      apiGet<{ customers: Customer[] }>(config, token, `/customers?q=${encodeURIComponent(q.trim())}`, ctl.signal).then((r) => setHits(r.customers), () => undefined);
    }, 250);
    return () => { clearTimeout(h); ctl.abort(); };
  }, [existing, q, config, token]);
  useEffect(() => { if (!t.locationId && shop) setT((x) => ({ ...x, locationId: shop.id })); }, [shop, t.locationId]);

  const pick = (cu: Customer) => { setPicked(cu); setHits([]); setQ(""); setAddressId(cu.addresses[0]?.id ?? "new"); };
  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const r = await apiSend<{ ticketNo: number; notice: string }>(config, token, "POST", "/tickets", {
        customer: picked ? { id: picked.id } : c,
        address: picked && addressId !== "new" ? { id: addressId } : a,
        ...t,
      });
      onClose();
      navigate(`/tickets/${r.ticketNo}`, { state: { notice: r.notice } });
    } catch (err) {
      setError((err as Error).message);
    } finally { setBusy(false); }
  };
  const setCv = (k: keyof typeof c) => (e: React.ChangeEvent<HTMLInputElement>) => setC({ ...c, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const setAv = (k: keyof typeof a) => (e: React.ChangeEvent<HTMLInputElement>) => setA({ ...a, [k]: e.target.value });

  return (
    <Modal title="Add Ticket" onClose={onClose}>
      <form className="mini-form add-ticket" onSubmit={(e) => void save(e)}>
        {wholesale.length > 0 && (
          <div className="quick-keys"><span className="lbl">Wholesale Customers</span>
            {wholesale.map((w) => <button key={w.id} type="button" className={`btn outline sm${picked?.id === w.id ? " on" : ""}`} onClick={() => { setExisting(false); pick(w); }}>{nameOf(w)}</button>)}
          </div>
        )}
        <h4>Customer</h4>
        {picked ? (
          <div className="picked"><strong>{nameOf(picked)}</strong>{picked.isWholesale && <span className="tag">Wholesale</span>}
            <span className="hint">{picked.email} · {picked.phone}</span>
            <button type="button" className="link-btn" onClick={() => { setPicked(null); setAddressId("new"); }}>Change</button></div>
        ) : (<>
          <label className="switch-row"><input type="checkbox" checked={existing} onChange={(e) => setExisting(e.target.checked)} /> Use existing customer information</label>
          {existing ? (
            <div className="cust-search">
              <label className="with-icon"><Search size={15} /><input autoFocus placeholder="Search name, email or phone (3+ characters)" value={q} onChange={(e) => setQ(e.target.value)} /></label>
              {hits.length > 0 && <ul className="hits">{hits.map((h) => <li key={h.id}><button type="button" onClick={() => pick(h)}><strong>{nameOf(h)}</strong> <span className="hint">{h.email} · {h.phone}</span></button></li>)}</ul>}
              {q.trim().length >= 3 && !hits.length && <p className="hint">No customers found.</p>}
            </div>
          ) : (<>
            <div className="two"><label>First name<input required value={c.firstName} onChange={setCv("firstName")} /></label>
              <label>Last name<input required value={c.lastName} onChange={setCv("lastName")} /></label></div>
            <div className="two"><label>Email<input type="email" required value={c.email} onChange={setCv("email")} /></label>
              <label>Phone<input type="tel" required value={c.phone} onChange={setCv("phone")} /></label></div>
            <label className="switch-row"><input type="checkbox" checked={c.isWholesale} onChange={setCv("isWholesale")} /> Wholesale customer</label>
            <p className="hint">If this email is already a customer, that customer is used (name and phone updated).</p>
          </>)}
        </>)}

        <h4>Address</h4>
        {picked && picked.addresses.length > 0 && (
          <label>Ship to<select value={addressId} onChange={(e) => setAddressId(e.target.value)}>
            {picked.addresses.map((ad) => <option key={ad.id} value={ad.id}>{addrLine(ad)}</option>)}
            <option value="new">A new address…</option>
          </select></label>
        )}
        {(!picked || addressId === "new") && (<>
          <label>Street<input required value={a.street} onChange={setAv("street")} /></label>
          <label>Street 2<input value={a.street2} onChange={setAv("street2")} /></label>
          <div className="three"><label>City<input required value={a.city} onChange={setAv("city")} /></label>
            <label>State<input required value={a.state} onChange={setAv("state")} maxLength={20} /></label>
            <label>Zip<input required value={a.zip} onChange={setAv("zip")} /></label></div>
        </>)}

        <h4>Ticket</h4>
        <label>Location<select value={t.locationId} onChange={(e) => setT({ ...t, locationId: e.target.value })}>
          {lookups?.locations.filter((l) => l.isActive).map((l) => <option key={l.id} value={l.id}>{l.name}{l.kind === "watchmaker" ? " (watchmaker)" : ""}</option>)}
        </select></label>
        <div className="two"><label>Job number<input value={t.jobNumber} onChange={(e) => setT({ ...t, jobNumber: e.target.value })} /></label>
          <label>Job name<input value={t.jobName} onChange={(e) => setT({ ...t, jobName: e.target.value })} /></label></div>
        <label>Description of repairs<textarea rows={3} value={t.description} onChange={(e) => setT({ ...t, description: e.target.value })} /></label>
        <p className="hint">Saving buys the customer's free shipping label and emails it (not for wholesale customers with email off). Test mode: only you receive emails.</p>
        {error && <p className="warn">{error}</p>}
        <div className="btn-row">
          <button type="submit" className="btn primary" disabled={busy || (existing && !picked)}>{busy ? "Creating… (about 10 seconds)" : "Save"}</button>
          <button type="button" className="btn outline" onClick={onClose} disabled={busy}>Cancel</button>
        </div>
      </form>
    </Modal>
  );
}

type Detector = { detect(src: CanvasImageSource): Promise<{ rawValue: string }[]> };
declare global { interface Window { BarcodeDetector?: new (o: { formats: string[] }) => Detector } }

/**
 * *QR*: scan the QR code on a packing slip (ticket number) or check-in slip (ticket id) with the camera, or type
 * / scan a ticket number with a handheld scanner, and the ticket opens.
 */
export function QrDialog({ config, onClose }: { config: PortalConfig; onClose: () => void }) {
  const auth = useAuth();
  const navigate = useNavigate();
  const video = useRef<HTMLVideoElement>(null);
  const [typed, setTyped] = useState("");
  const [msg, setMsg] = useState("");
  const done = useRef(false);

  const open = async (raw: string) => {
    const v = raw.trim().replace(/^#/, "");
    if (done.current || !v) return;
    if (/^\d{3,9}$/.test(v)) { done.current = true; onClose(); navigate(`/tickets/${v}`); return; }
    const id = v.match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i)?.[0];
    if (!id) { setMsg(`"${v.slice(0, 40)}" is not a ticket code.`); return; }
    done.current = true;
    try {
      const d = await apiGet<{ ticket: { ticketNo: number } }>(config, auth.user?.id_token ?? "", `/tickets/${id}`);
      onClose(); navigate(`/tickets/${d.ticket.ticketNo}`);
    } catch { done.current = false; setMsg("No ticket has that code."); }
  };

  useEffect(() => {
    if (!window.BarcodeDetector) { setMsg("This browser can't read QR codes with the camera. Type or scan the ticket number instead."); return; }
    let stream: MediaStream | null = null;
    let timer = 0;
    const detector = new window.BarcodeDetector({ formats: ["qr_code"] });
    navigator.mediaDevices?.getUserMedia({ video: { facingMode: "environment" } }).then((s) => {
      stream = s;
      if (video.current) { video.current.srcObject = s; void video.current.play(); }
      const tick = async () => {
        if (video.current && video.current.readyState >= 2) {
          const codes = await detector.detect(video.current).catch(() => []);
          if (codes[0]) { void open(codes[0].rawValue); return; }
        }
        timer = window.setTimeout(() => void tick(), 300);
      };
      void tick();
    }, () => setMsg("The camera is not available. Type or scan the ticket number instead."));
    return () => { window.clearTimeout(timer); stream?.getTracks().forEach((t) => t.stop()); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Modal title="Scan a ticket" onClose={onClose}>
      <div className="mini-form qr">
        <video ref={video} muted playsInline className="qr-video" />
        <form onSubmit={(e) => { e.preventDefault(); void open(typed); }} className="btn-row">
          <input autoFocus placeholder="Ticket number" value={typed} onChange={(e) => setTyped(e.target.value)} aria-label="Ticket number" />
          <button type="submit" className="btn primary">Open</button>
        </form>
        {msg ? <p className="warn">{msg}</p> : <p className="hint">Hold the slip's QR code up to the camera.</p>}
      </div>
    </Modal>
  );
}

export function TicketListButtons({ config, lookups }: { config: PortalConfig; lookups: Lookups | null }) {
  const [dialog, setDialog] = useState<"add" | "qr" | null>(null);
  return (
    <>
      <button type="button" className="tb-btn" onClick={() => setDialog("qr")} title="Scan a slip's QR code to open its ticket"><QrCode size={16} />*QR*</button>
      <button type="button" className="tb-btn primary" onClick={() => setDialog("add")}>+ Add Ticket</button>
      {dialog === "add" && <AddTicketDialog config={config} lookups={lookups} onClose={() => setDialog(null)} />}
      {dialog === "qr" && <QrDialog config={config} onClose={() => setDialog(null)} />}
    </>
  );
}
